import React from 'react';
import LightboxGallery from './LightboxGallery';
import { resolveFileUrl } from '../utils/fileUrl';
import { parseProofs, isImageUrl } from '../utils/proofs';

/**
 * Read-only renderer for a ledger entry's proof(s).
 *
 * A proof value may hold one URL (rows written before multi-proof existed) or a
 * JSON list of up to 5 — parseProofs() flattens both. Images go into the shared
 * LightboxGallery (thumbnail + "+N" badge + full-screen viewer); PDFs and other
 * non-images can't be rendered as <img>, so they get a chip that opens in a new
 * tab, which is how proofs have always been viewed.
 */
interface Props {
  value?: string | null;
  /** Shown at the top of the lightbox, e.g. "Ledger #42". */
  caption?: string;
  thumbnailSize?: number;
}

const ProofCell: React.FC<Props> = ({ value, caption, thumbnailSize = 28 }) => {
  const proofs = parseProofs(value);
  if (proofs.length === 0) return <span style={{ color: '#cbd5e1' }}>-</span>;

  const images = proofs.filter(isImageUrl).map(resolveFileUrl);
  const files = proofs.filter((u) => !isImageUrl(u));

  return (
    // Ledger rows open the editor on click — keep proof viewing from triggering it.
    <span
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', lineHeight: 0 }}
    >
      {images.length > 0 && (
        <LightboxGallery images={images} thumbnailSize={thumbnailSize} alt="Proof" caption={caption} />
      )}
      {files.map((url, i) => (
        <a
          key={url + i}
          href={resolveFileUrl(url)}
          target="_blank"
          rel="noopener noreferrer"
          title={`Open file ${i + 1}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '2px',
            background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '4px',
            padding: '1px 4px', fontSize: '0.7rem', fontWeight: 700,
            color: '#475569', textDecoration: 'none', lineHeight: 1.4,
          }}
        >
          📄{files.length > 1 ? i + 1 : ''}
        </a>
      ))}
    </span>
  );
};

export default ProofCell;
